import { useMutation } from "@tanstack/react-query";
import {
  EditProjectResponse,
  ManageProjectRequest,
  UniqueCheckResponse,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { NotUniqueError } from "./errors.ts";
import { useEditProjectCache } from "./useEditProjectCache.ts";

export const useEditProjectPermissions = () => {
  const { getToken } = useAuthentication();
  const editProjectCache = useEditProjectCache();
  const { error, isPending, mutate, isError, reset } = useMutation({
    mutationFn: async (
      project: ManageProjectRequest,
    ): Promise<EditProjectResponse> => {
      const token = await getToken();

      const uniqueCheckResponse = await fetch(
        `/api/projects/unique?displayName=${encodeURIComponent(project.displayName)}&currProjectId=${project.id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!uniqueCheckResponse.ok) {
        throw new Error("Checking the project name failed");
      }

      const { isUnique } = UniqueCheckResponse.parse(
        await uniqueCheckResponse.json(),
      );

      if (!isUnique) {
        throw new NotUniqueError();
      }

      const response = await fetch(`/api/projects/${project.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(project),
      });

      if (!response.ok) {
        throw new Error("Saving the project failed");
      }

      return EditProjectResponse.parse(await response.json());
    },
    onSuccess: (data, project) => editProjectCache(project, data),
  });

  return { mutate, isPending, error, isError, reset };
};
